import { useState, useEffect, useCallback } from 'react';
import * as dateFns from 'date-fns';
import { BookingApi } from 'shared/api';
import { DayInWeekEnum } from 'shared/api/booking/interfaces/calendar-week-info-type.interface';
import { dayCalendarStore } from 'entities/index';

export interface IWeek {
  date: Date;
  dayNumber: number;
  type: DayInWeekEnum;
}

export const useWeeklyCalendar = (
  initialDate: Date,
  pointId: string | undefined
) => {
  let selectedDate = dayCalendarStore.getSelectedDateAsDate;
  selectedDate = initialDate;
  const [weekNumber, setWeekNumber] = useState<number>(
    dateFns.getISOWeek(initialDate)
  );
  const [week, setWeek] = useState<IWeek[]>([]);
  // Опционально: можно добавить стейт isLoading для лоадеров
  // const [isLoading, setIsLoading] = useState(false);

  const fetchWeekDays = useCallback(
    async (targetWeekNum: number, targetDate: Date) => {
      if (!pointId) return;

      // Безопасный расчет года на основе текущей даты, а не системного времени
      const year = dateFns.getISOWeekYear(targetDate);
      const startOfYear = dateFns.startOfISOWeekYear(targetDate);
      const dateInWeek = dateFns.setISOWeek(startOfYear, targetWeekNum);
      const startOfWeekDate = dateFns.startOfISOWeek(dateInWeek);

      try {
        const weekData = await BookingApi.getCalendarWeekInfo({
          year,
          weekNumber: targetWeekNum,
          pointId: pointId,
        });

        const mappedWeek = Array.from({ length: 7 }).map((_, index) => {
          const day = dateFns.addDays(startOfWeekDate, index);
          const dayNumber = Number(dateFns.format(day, 'd'));
          return {
            date: day,
            dayNumber,
            type: weekData[dayNumber],
          };
        });

        setWeek(mappedWeek);
      } catch (error) {
        console.error('Failed to fetch calendar week info:', error);
      }
    },
    [pointId]
  );

  const changeWeekHandler = useCallback(
    (change: number) => {
      setWeekNumber((prev) => {
        const nextWeek = prev + change;
        // При смене недели сохраняем текущую дату как ориентир для расчета года
        fetchWeekDays(nextWeek, selectedDate);
        return nextWeek;
      });
    },
    [fetchWeekDays, selectedDate]
  );

  const selectedDateHandler = useCallback(
    (date: Date) => {
      const nextWeek = dateFns.getISOWeek(date);
      selectedDate = date;
      if (nextWeek !== weekNumber) {
        setWeekNumber(nextWeek);
        fetchWeekDays(nextWeek, date);
      }
    },
    [weekNumber, fetchWeekDays]
  );

  // Первичная загрузка или реакция на смену pointId
  useEffect(() => {
    fetchWeekDays(weekNumber, selectedDate);
  }, [fetchWeekDays, pointId]); // Убрали weekNumber из зависимостей, чтобы избежать лишних запросов

  return {
    selectedDate,
    week,
    changeWeekHandler,
    selectedDateHandler,
  };
};
